import { useCallback, useEffect, useRef, useState } from "react";

import styles from "./team-stack.module.css";
import { type TeamPhoto } from "./types.ts";

export interface TeamStackProps {
  photos: TeamPhoto[];
  /** Auto-cycle interval in ms when there is no user interaction. */
  autoCycleMs?: number;
}

type Slot = "center" | "left" | "right" | "hiddenLeft" | "hiddenRight";

const SLOT_CLASS: Record<Slot, string> = {
  center: "",
  left: "",
  right: "",
  hiddenLeft: "",
  hiddenRight: "",
};

export function TeamStack({ photos, autoCycleMs = 6000 }: TeamStackProps) {
  // Initialize slot class lookup against module CSS (lazy via styles).
  SLOT_CLASS.center = styles.slotCenter;
  SLOT_CLASS.left = styles.slotLeft;
  SLOT_CLASS.right = styles.slotRight;
  SLOT_CLASS.hiddenLeft = styles.slotHiddenLeft;
  SLOT_CLASS.hiddenRight = styles.slotHiddenRight;

  const photoCount = photos.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const autoCycleRef = useRef<number | null>(null);

  const goNext = useCallback(() => {
    setActiveIndex((i) => (i + 1) % Math.max(photoCount, 1));
  }, [photoCount]);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + photoCount) % Math.max(photoCount, 1));
  }, [photoCount]);

  const goTo = useCallback(
    (idx: number) => {
      setActiveIndex(((idx % photoCount) + photoCount) % photoCount);
    },
    [photoCount],
  );

  /* Auto-cycle */
  useEffect(() => {
    if (photoCount <= 1) return;
    if (autoCycleRef.current != null) {
      window.clearTimeout(autoCycleRef.current);
    }
    autoCycleRef.current = window.setTimeout(() => {
      goNext();
    }, autoCycleMs);
    return () => {
      if (autoCycleRef.current != null)
        window.clearTimeout(autoCycleRef.current);
    };
  }, [activeIndex, autoCycleMs, photoCount, goNext]);

  if (photoCount === 0) return null;

  /** Compute the slot for a photo based on its signed offset from active. */
  function getSlot(photoIdx: number): Slot {
    const half = Math.floor(photoCount / 2);
    let diff = (photoIdx - activeIndex) % photoCount;
    if (diff < -half) diff += photoCount;
    if (diff > half) diff -= photoCount;
    if (diff === 0) return "center";
    if (diff === 1) return "right";
    if (diff === -1) return "left";
    return diff > 0 ? "hiddenRight" : "hiddenLeft";
  }

  return (
    <div className={styles.root}>
      <div className={styles.carousel}>
        {photos.map((photo, idx) => {
          const slot = getSlot(idx);
          const isCenter = slot === "center";
          const isClickable = slot === "left" || slot === "right";
          return (
            <div
              key={idx}
              className={`${styles.card} ${SLOT_CLASS[slot]}`}
              onClick={
                slot === "left"
                  ? goPrev
                  : slot === "right"
                    ? goNext
                    : undefined
              }
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : -1}
              aria-hidden={!isCenter}
              aria-label={
                isClickable
                  ? slot === "left"
                    ? "Previous photo"
                    : "Next photo"
                  : undefined
              }
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (slot === "left") goPrev();
                        else goNext();
                      }
                    }
                  : undefined
              }
            >
              <div
                className={styles.photo}
                style={
                  photo.src == null
                    ? { background: photo.gradient ?? "var(--color-slate-200)" }
                    : undefined
                }
              >
                {photo.src != null ? (
                  <img
                    className={styles.photoImg}
                    src={photo.src}
                    alt=""
                    draggable={false}
                  />
                ) : photo.emoji != null ? (
                  <span className={styles.photoEmoji} aria-hidden="true">
                    {photo.emoji}
                  </span>
                ) : null}
              </div>
              <div className={styles.caption}>
                <span className={styles.captionText}>{photo.caption}</span>
                {photo.year != null ? (
                  <span className={styles.captionYear}>{photo.year}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {photoCount > 1 ? (
        <div className={styles.dots} role="tablist" aria-label="Photo carousel">
          {photos.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`${styles.dot} ${
                idx === activeIndex ? styles.dotActive : ""
              }`}
              onClick={() => goTo(idx)}
              aria-label={`Show photo ${idx + 1} of ${photoCount}`}
              aria-selected={idx === activeIndex}
              role="tab"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
